/**
 * Whitelabel-safe analytics bootstrap.
 *
 * Supports two providers, both env-gated and no-op if no key is set:
 *  - PostHog: REACT_APP_POSTHOG_KEY (+ optional REACT_APP_POSTHOG_HOST, defaults to https://eu.i.posthog.com)
 *  - Google Analytics 4: REACT_APP_GA_ID (e.g. G-XXXXXXXXXX)
 *
 * Both load scripts at runtime — never via index.html — so the inline
 * `whitelabel.js` MutationObserver continues to scrub the Emergent badge
 * uninterrupted. We tag the injected <script>s with data-uid-analytics="1"
 * so the scrubber will see and ignore them.
 *
 * The provider is a no-op when both env vars are unset, keeping development
 * builds and self-hosted setups clean.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
const GA_ID = process.env.REACT_APP_GA_ID || "";

let _initialised = false;

function initPostHog() {
  if (!POSTHOG_KEY || typeof window === "undefined" || window.posthog) return;
  // Lightweight inline snippet from PostHog docs (truncated to essentials).
  window.posthog = window.posthog || [];
  const ph = window.posthog;
  ph._i = [];
  ph.init = function (k, c) { ph._i.push([k, c]); };
  ph.capture = function (...args) { (ph._q = ph._q || []).push(["capture", args]); };
  ph.identify = function (...args) { (ph._q = ph._q || []).push(["identify", args]); };
  const s = document.createElement("script");
  s.async = true;
  s.src = `${POSTHOG_HOST.replace(/\/$/, "")}/static/array.js`;
  s.dataset.uidAnalytics = "1";
  s.onload = () => {
    if (window.posthog && window.posthog.init) {
      window.posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: false, // we trigger pageviews manually for SPA routing
        disable_session_recording: true,
        autocapture: false,
        persistence: "localStorage",
      });
    }
  };
  document.head.appendChild(s);
}

function initGA() {
  if (!GA_ID || typeof window === "undefined" || window.gtag) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  s.dataset.uidAnalytics = "1";
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
}

function init() {
  if (_initialised) return;
  _initialised = true;
  initPostHog();
  initGA();
}

function pageView(path) {
  if (!path) return;
  try { window.posthog?.capture?.("$pageview", { $current_url: window.location.href }); } catch (e) { /* ignore */ }
  try { window.gtag?.("event", "page_view", { page_path: path, page_location: window.location.href }); } catch (e) { /* ignore */ }
}

/** Mount once at the root of the app — listens to React Router location changes. */
export default function AnalyticsProvider({ children }) {
  const location = useLocation();
  const lastPath = useRef("");
  useEffect(() => { init(); }, []);
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    pageView(path);
  }, [location]);
  return children;
}

/** Programmatic event helper — safe to call from anywhere; no-op if disabled. */
export function trackEvent(name, props = {}) {
  try { window.posthog?.capture?.(name, props); } catch (e) { /* ignore */ }
  try { window.gtag?.("event", name, props); } catch (e) { /* ignore */ }
}

export const analyticsEnabled = !!(POSTHOG_KEY || GA_ID);
